import { datadogRum } from '@datadog/browser-rum';

const datadogConfig = {
  applicationId: process.env.VUE_APP_DD_APPLICATION_ID,
  clientToken: process.env.VUE_APP_DD_CLIENT_TOKEN,
  site: process.env.VUE_APP_DD_SITE || 'us3.datadoghq.com',
  service: process.env.VUE_APP_DD_SERVICE || 'onlypay-conta-web',
  env: process.env.VUE_APP_DD_ENV || process.env.NODE_ENV || 'development',
  version: process.env.VUE_APP_DD_VERSION,
};

let isRumInitialized = false;
let isGlobalTrackingSetup = false;

function parseSampleRate(value, fallback) {
  if (value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function isDatadogEnabled() {
  const value = process.env.VUE_APP_DD_ENABLED;
  if (value === undefined || value === '') return true;
  return !['false', '0', 'off', 'no'].includes(String(value).trim().toLowerCase());
}

function getAllowedTracingUrls() {
  const urls = [
    process.env.VUE_APP_HUB_BASE_URL,
    process.env.VUE_APP_HUB_BASE_URL2,
  ].filter(Boolean);
  const origins = new Set();
  urls.forEach((url) => {
    try {
      origins.add(new URL(url).origin);
    } catch (e) {
      // ignore invalid URL
    }
  });
  return Array.from(origins);
}

export function isDatadogRumActive() {
  return isRumInitialized;
}

function mapUserToDatadog(user) {
  if (!user || typeof user !== 'object') return null;

  const id = user.idConta || user.id || user.idConductor;
  if (id === undefined || id === null || id === '') return null;

  const mapped = { id: String(id) };
  const { nome, name: userName, email } = user;
  const name = nome || userName;

  if (name) mapped.name = String(name);
  if (email) mapped.email = String(email);

  return mapped;
}

export function identifyDatadogUser(user) {
  if (!isRumInitialized) return;

  const mapped = mapUserToDatadog(user);
  if (!mapped) return;

  datadogRum.setUser(mapped);
}

export function clearDatadogUser() {
  if (!isRumInitialized) return;
  datadogRum.clearUser();
}

export function reportDatadogError(error, context = {}) {
  if (!isRumInitialized || !error) return;

  const err = error instanceof Error ? error : new Error(String(error));
  datadogRum.addError(err, context);
}

function sanitizeRequestUrl(url) {
  if (!url) return '';

  try {
    const parsed = /^https?:\/\//i.test(url)
      ? new URL(url)
      : new URL(url, window.location.origin);

    ['token', 'code', 'senha', 'password', 'authorization'].forEach((param) => {
      parsed.searchParams.delete(param);
    });

    return `${parsed.pathname}${parsed.search}`;
  } catch (e) {
    return String(url).split('?')[0];
  }
}

function getHttpMethod(config) {
  return (config && config.method ? config.method : 'get').toUpperCase();
}

function getHttpStatus(response, error) {
  if (response && response.status) return response.status;
  if (error && error.response && error.response.status) return error.response.status;
  return 0;
}

export function trackDatadogApiCall(config, response, error) {
  if (!isRumInitialized || !config) return;

  const url = sanitizeRequestUrl(config.url);
  const method = getHttpMethod(config);
  const status = getHttpStatus(response, error);
  const durationMs = config.metadata && config.metadata.ddStartTime
    ? Date.now() - config.metadata.ddStartTime
    : undefined;

  const context = {
    http: {
      method,
      url,
      status_code: status,
    },
  };

  if (durationMs !== undefined) {
    context.http.duration_ms = durationMs;
  }

  if (error) {
    context.error = {
      message: error.message || 'HTTP request failed',
    };
  }

  datadogRum.addAction(`api:${method}:${status || 'network'}`, context);

  const isServerOrNetworkFailure = Boolean(error) || status >= 500 || status === 0;
  if (!isServerOrNetworkFailure) return;

  const apiError = error instanceof Error
    ? error
    : new Error(`HTTP ${status} ${method} ${url}`);

  reportDatadogError(apiError, {
    source: 'http',
    ...context,
  });
}

export function installDatadogHttpTracking(axiosInstance) {
  if (!isRumInitialized || !axiosInstance || !axiosInstance.interceptors) return;

  axiosInstance.interceptors.request.use(
    (config) => {
      if (!config.metadata) {
        config.metadata = {};
      }
      config.metadata.ddStartTime = Date.now();
      return config;
    },
    (err) => Promise.reject(err)
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      trackDatadogApiCall(response.config, response);
      return response;
    },
    (err) => {
      if (err && err.config) {
        trackDatadogApiCall(err.config, err.response, err);
      }
      return Promise.reject(err);
    }
  );
}

function setupConsoleErrorTracking() {
  if (!isRumInitialized || typeof console === 'undefined') return;

  const originalConsoleError = console.error.bind(console);

  console.error = (...args) => {
    originalConsoleError(...args);

    const errorArg = args.find((arg) => arg instanceof Error);
    if (errorArg) {
      reportDatadogError(errorArg, { source: 'console.error' });
      return;
    }

    const message = args
      .map((arg) => {
        if (arg instanceof Error) return arg.message;
        if (typeof arg === 'string') return arg;
        try {
          return JSON.stringify(arg);
        } catch (e) {
          return String(arg);
        }
      })
      .filter(Boolean)
      .join(' ');

    if (message) {
      reportDatadogError(new Error(message), { source: 'console.error' });
    }
  };
}

export function setupDatadogGlobalTracking(Vue) {
  if (!isRumInitialized || isGlobalTrackingSetup) return;
  isGlobalTrackingSetup = true;

  setupConsoleErrorTracking();
  // Uncaught exceptions e unhandled rejections já são coletados pelo SDK RUM.

  if (!Vue) return;

  const previousErrorHandler = Vue.config.errorHandler;

  Vue.config.errorHandler = (err, vm, info) => {
    reportDatadogError(err, {
      source: 'vue',
      vueInfo: info,
      component: vm && vm.$options && vm.$options.name,
    });

    if (typeof previousErrorHandler === 'function') {
      previousErrorHandler(err, vm, info);
    }
  };
}

function resolveViewName(route) {
  if (route) {
    return String(
      route.name || (route.meta && route.meta.title) || route.path || 'unknown_route'
    );
  }

  const { pathname, hash } = window.location;
  return hash && hash.length > 1 ? hash.replace(/^#/, '') : pathname || 'initial_load';
}

function trackInitialDatadogView() {
  if (!isRumInitialized) return;
  datadogRum.startView({ name: resolveViewName() });
}

export function trackDatadogView(route) {
  if (!isRumInitialized) return;

  datadogRum.startView({ name: resolveViewName(route) });
}

function refreshRumActiveState() {
  if (typeof datadogRum.getInternalContext === 'function') {
    isRumInitialized = Boolean(datadogRum.getInternalContext());
    return isRumInitialized;
  }

  return isRumInitialized;
}

function scheduleRumStartupChecks() {
  // O SDK expõe initConfiguration antes da sessão subir; revalida após o bootstrap assíncrono.
  const delays = [0, 50, 250, 1000];
  delays.forEach((delay) => {
    setTimeout(() => {
      if (!refreshRumActiveState()) {
        trackInitialDatadogView();
        refreshRumActiveState();
      }
    }, delay);
  });
}

export function initializeDatadogRum() {
  if (typeof window === 'undefined') return false;
  if (isRumInitialized) return true;
  if (!isDatadogEnabled()) return false;

  if (!datadogConfig.applicationId || !datadogConfig.clientToken) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[Datadog RUM] applicationId ou clientToken ausente no build.');
    }
    return false;
  }

  const initOptions = {
    applicationId: datadogConfig.applicationId,
    clientToken: datadogConfig.clientToken,
    site: datadogConfig.site,
    service: datadogConfig.service,
    env: datadogConfig.env,
    // Mesmo padrão do console-vuejs (que envia eventos): views automáticas + startView no router.
    trackViewsManually: false,
    trackingConsent: 'granted',
    sessionSampleRate: 100,
    sessionReplaySampleRate: parseSampleRate(
      process.env.VUE_APP_DD_SESSION_REPLAY_SAMPLE_RATE,
      20
    ),
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: getAllowedTracingUrls(),
    traceSampleRate: parseSampleRate(process.env.VUE_APP_DD_TRACE_SAMPLE_RATE, 100),
    compressIntakeRequests: false,
    sessionPersistence: 'local-storage',
  };

  if (datadogConfig.version) {
    initOptions.version = datadogConfig.version;
  }

  datadogRum.init(initOptions);
  trackInitialDatadogView();
  refreshRumActiveState();
  scheduleRumStartupChecks();
  return isRumInitialized;
}
